import React, { useState, useEffect } from 'react';
import { Card, Form, Row, Col, Button, Alert, Modal } from 'react-bootstrap';
import { apiService } from '../services/api';
import { Snapshot, CreateDemoRequest } from '../types/Demo';

interface CreateDemoProps {
  onDemoCreated: () => void;
}

const CreateDemo: React.FC<CreateDemoProps> = ({ onDemoCreated }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [setupMode, setSetupMode] = useState<'theme' | 'snapshot'>('theme');
  const [selectedTheme, setSelectedTheme] = useState('');
  const [selectedSnapshot, setSelectedSnapshot] = useState('');
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'danger' | 'info'; message: string } | null>(null);
  const [isLoadingSnapshots, setIsLoadingSnapshots] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  // Edit snapshot states
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSnapshot, setEditingSnapshot] = useState<Snapshot | null>(null);
  const [editSnapshotName, setEditSnapshotName] = useState('');
  const [editSnapshotImage, setEditSnapshotImage] = useState<File | null>(null);
  const [isEditingSnapshot, setIsEditingSnapshot] = useState(false);

  // Delete snapshot states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingSnapshot, setDeletingSnapshot] = useState<Snapshot | null>(null);
  const [isDeletingSnapshot, setIsDeletingSnapshot] = useState(false);

  useEffect(() => {
    if (setupMode === 'snapshot') {
      loadSnapshots();
    }
  }, [setupMode]);

  const loadSnapshots = async () => {
    console.log('📥 Loading snapshots...');
    setIsLoadingSnapshots(true);
    try {
      const snapshotsData = await apiService.getSnapshots();
      console.log('✅ Snapshots loaded:', snapshotsData.length, 'snapshots');
      console.log('📋 Sample snapshot data:', snapshotsData[0]);
      setSnapshots(snapshotsData);
    } catch (error) {
      console.error('❌ Error loading snapshots:', error);
      showAlert('danger', 'Snapshot\'lar yüklenirken hata oluştu');
    } finally {
      setIsLoadingSnapshots(false);
    }
  };

  const showAlert = (type: 'success' | 'danger' | 'info', message: string) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000);
  };

  const generatePassword = () => {
    const length = 12;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let newPassword = "";
    for (let i = 0; i < length; i++) {
      newPassword += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    setPassword(newPassword);
    setShowPassword(true);
    showAlert('info', 'Güçlü şifre oluşturuldu');
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    const formData: CreateDemoRequest = {
      email,
      password,
      setupMode,
    };

    if (setupMode === 'theme') {
      if (!selectedTheme) {
        showAlert('danger', 'Lütfen bir tema seçiniz');
        return;
      }
      formData.theme = selectedTheme;
    } else {
      if (!selectedSnapshot) {
        showAlert('danger', 'Lütfen bir snapshot seçiniz');
        return;
      }
      formData.snapshot = selectedSnapshot;
    }

    setIsSubmitting(true);

    try {
      const result = await apiService.createDemo(formData);
      showAlert('success', `Demo site başarıyla oluşturuldu! Domain: ${result.demoConfig.domain}`);
      
      // Reset form
      setEmail('');
      setPassword('');
      setSelectedTheme('');
      setSelectedSnapshot('');
      setSetupMode('theme');
      
      // Refresh demos list
      setTimeout(() => {
        onDemoCreated();
      }, 1000);
      
    } catch (error: any) {
      showAlert('danger', error.response?.data?.error || 'Bir hata oluştu');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const openEditModal = (snapshot: Snapshot) => {
    console.log('🔓 Opening edit modal for snapshot:', {
      id: snapshot.id,
      name: snapshot.name,
      displayName: snapshot.displayName,
      isLocked: snapshot.isLocked
    });
    setEditingSnapshot(snapshot);
    setEditSnapshotName(snapshot.displayName);
    setEditSnapshotImage(null);
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingSnapshot(null);
    setEditSnapshotName('');
    setEditSnapshotImage(null);
  };

  const handleEditSnapshot = async () => {
    if (!editingSnapshot) {
      console.log('⚠️ No editing snapshot found');
      return;
    }

    console.log('📝 Edit snapshot data:', {
      id: editingSnapshot.id,
      currentDisplayName: editingSnapshot.displayName,
      newDisplayName: editSnapshotName,
      hasNewImage: !!editSnapshotImage
    });

    // Check if snapshot has a valid ID
    if (!editingSnapshot.id) {
      console.error('❌ Snapshot ID is missing:', editingSnapshot);
      showAlert('danger', 'Snapshot ID bulunamadı. Lütfen sayfayı yenileyip tekrar deneyin.');
      return;
    }

    // Check if there are any changes
    const isNameChanged = editSnapshotName !== editingSnapshot.displayName;
    const hasNewImage = !!editSnapshotImage;

    console.log('🔍 Change detection:', { isNameChanged, hasNewImage });

    if (!isNameChanged && !hasNewImage) {
      console.log('ℹ️ No changes detected, closing modal');
      showAlert('info', 'Hiçbir değişiklik yapılmadı');
      closeEditModal();
      return;
    }

    setIsEditingSnapshot(true);
    try {
      // Update display name if changed (using snapshot ID)
      if (isNameChanged) {
        console.log('🔄 Updating display name...', { snapshotId: editingSnapshot.id, newName: editSnapshotName });
        await apiService.updateSnapshotName(editingSnapshot.id, editSnapshotName);
        console.log('✅ Display name updated successfully');
        showAlert('success', 'Snapshot ismi güncellendi');
      }

      // Update screenshot if new file selected (using snapshot ID)
      if (hasNewImage) {
        console.log('🖼️ Updating screenshot...', { snapshotId: editingSnapshot.id, fileName: editSnapshotImage?.name });
        await apiService.updateSnapshotScreenshot(editingSnapshot.id, editSnapshotImage!);
        console.log('✅ Screenshot updated successfully');
        showAlert('success', 'Snapshot görseli güncellendi');
      }

      // Reload snapshots
      console.log('🔄 Reloading snapshots...');
      await loadSnapshots();
      closeEditModal();
    } catch (error: any) {
      console.error('❌ Edit snapshot error:', error);
      showAlert('danger', error.response?.data?.error || 'Güncelleme başarısız');
    } finally {
      setIsEditingSnapshot(false);
    }
  };

  const openDeleteConfirm = (snapshot: Snapshot) => {
    setDeletingSnapshot(snapshot);
    setShowDeleteConfirm(true);
  };

  const closeDeleteConfirm = () => {
    setShowDeleteConfirm(false);
    setDeletingSnapshot(null);
  };

  const handleDeleteSnapshot = async () => {
    if (!deletingSnapshot) {
      console.log('⚠️ No snapshot to delete');
      return;
    }

    // Check if snapshot has a valid ID
    if (!deletingSnapshot.id) {
      console.error('❌ Snapshot ID is missing for delete:', deletingSnapshot);
      showAlert('danger', 'Snapshot ID bulunamadı. Lütfen sayfayı yenileyip tekrar deneyin.');
      return;
    }

    console.log('🗑️ Deleting snapshot:', { id: deletingSnapshot.id, name: deletingSnapshot.name });

    setIsDeletingSnapshot(true);
    try {
      // Use snapshot ID for delete
      await apiService.deleteSnapshot(deletingSnapshot.id);
      console.log('✅ Snapshot deleted successfully');
      showAlert('success', 'Snapshot başarıyla silindi');

      // Reload snapshots
      await loadSnapshots();
      closeDeleteConfirm();
    } catch (error: any) {
      console.error('❌ Delete snapshot error:', error);
      showAlert('danger', error.response?.data?.error || 'Silme işlemi başarısız');
    } finally {
      setIsDeletingSnapshot(false);
    }
  };

  const handleDownloadSnapshot = async (snapshot: Snapshot) => {
    try {
      showAlert('info', 'Snapshot indiriliyor...');
      // Use name (file name without .zip) instead of displayName
      const snapshotName = snapshot.name.replace('.zip', '');
      await apiService.downloadSnapshot(snapshotName);
      showAlert('success', 'Snapshot başarıyla indirildi');
    } catch (error: any) {
      showAlert('danger', error.response?.data?.error || 'İndirme işlemi başarısız');
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.name.endsWith('.zip')) {
        showAlert('danger', 'Lütfen sadece .zip dosyası seçiniz');
        event.target.value = '';
        return;
      }

      // Validate file size (200MB max)
      const maxSize = 200 * 1024 * 1024; // 200MB
      if (file.size > maxSize) {
        showAlert('danger', `Dosya çok büyük. Maksimum boyut: 200MB. Seçilen: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
        event.target.value = '';
        return;
      }

      setUploadFile(file);
      showAlert('info', `Dosya seçildi: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)`);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      showAlert('danger', 'Lütfen bir ZIP dosyası seçiniz');
      return;
    }

    // Generate snapshot name from file name (without .zip extension)
    const snapshotName = uploadFile.name.replace('.zip', '').replace(/[^a-zA-Z0-9_-]/g, '_');

    setIsUploading(true);
    setUploadProgress(0);

    try {
      await apiService.uploadSnapshot(
        uploadFile,
        snapshotName,
        (progressEvent: any) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      );

      showAlert('success', `Snapshot başarıyla yüklendi: ${snapshotName}`);
      setUploadFile(null);
      setUploadProgress(0);

      // Refresh snapshots list
      await loadSnapshots();

      // Clear file input
      const fileInput = document.getElementById('snapshotFileInput') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
    } catch (error: any) {
      showAlert('danger', error.response?.data?.error || error.response?.data?.details || 'Upload başarısız oldu');
      setUploadProgress(0);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
    <Card>
      <Card.Body>
        <h5 className="card-title">
          <i className="bi bi-plus-circle"></i> Yeni Demo Site Oluştur
        </h5>

        {alert && (
          <Alert variant={alert.type} className="mt-3">
            {alert.message}
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-4">
                <Form.Label className="fw-semibold">
                  <i className="bi bi-envelope"></i> Email Adresi
                </Form.Label>
                <Form.Control
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@example.com"
                  required
                />
              </Form.Group>
            </Col>
            
            <Col md={6}>
              <Form.Group className="mb-4">
                <Form.Label className="fw-semibold">
                  <i className="bi bi-lock"></i> Şifre
                </Form.Label>
                <div className="input-group">
                  <Form.Control
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Şifrenizi girin"
                    required
                  />
                  <Button
                    variant="outline-secondary"
                    onClick={() => setShowPassword(!showPassword)}
                    title={showPassword ? "Şifreyi gizle" : "Şifreyi göster"}
                  >
                    <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`}></i>
                  </Button>
                  <Button
                    variant="outline-primary"
                    onClick={generatePassword}
                    title="Güçlü şifre oluştur"
                  >
                    <i className="bi bi-key"></i>
                  </Button>
                </div>
              </Form.Group>
            </Col>

            <Col md={12}>
              <Form.Group className="mb-4">
                <Form.Label className="fw-semibold">
                  <i className="bi bi-brush"></i> Kurulum Türü Seçimi
                </Form.Label>
                
                {/* Setup Mode Selection */}
                <Row className="mt-3 mb-3">
                  <Col md={6}>
                    <div 
                      className={`form-check h-100 p-4 border rounded cursor-pointer ${setupMode === 'theme' ? 'border-primary bg-light' : ''}`}
                      onClick={() => setSetupMode('theme')}
                      style={{ cursor: 'pointer', transition: 'all 0.3s ease' }}
                    >
                      <Form.Check
                        type="radio"
                        name="setupMode"
                        id="themeMode"
                        checked={setupMode === 'theme'}
                        onChange={() => setSetupMode('theme')}
                        label={
                          <>
                            <div className="fw-semibold">
                              <i className="bi bi-palette"></i> Tema ile Kur (Temiz)
                            </div>
                            <small className="text-muted">Boş tema ile başla</small>
                          </>
                        }
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div 
                      className={`form-check h-100 p-4 border rounded cursor-pointer ${setupMode === 'snapshot' ? 'border-primary bg-light' : ''}`}
                      onClick={() => setSetupMode('snapshot')}
                      style={{ cursor: 'pointer', transition: 'all 0.3s ease' }}
                    >
                      <Form.Check
                        type="radio"
                        name="setupMode"
                        id="snapshotMode"
                        checked={setupMode === 'snapshot'}
                        onChange={() => setSetupMode('snapshot')}
                        label={
                          <>
                            <div className="fw-semibold">
                              <i className="bi bi-camera"></i> Snapshot ile Kur (İçerikli)
                            </div>
                            <small className="text-muted">Hazır içerik ile başla</small>
                          </>
                        }
                      />
                    </div>
                  </Col>
                </Row>

                {/* Theme Selection */}
                {setupMode === 'theme' && (
                  <div>
                    <h6 className="fw-semibold mb-3">
                      <i className="bi bi-palette"></i> Tema Seçimi
                    </h6>
                    <Row>
                      {['template1', 'template2', 'template3'].map((theme, index) => (
                        <Col key={theme} md={4}>
                          <div 
                            className={`theme-option ${selectedTheme === theme ? 'selected' : ''}`}
                            onClick={() => setSelectedTheme(theme)}
                            style={{ cursor: 'pointer' }}
                          >
                            <div 
                              className="theme-preview"
                              style={{
                                backgroundImage: `url(${String(index + 1).padStart(3, '0')}.jpg)`,
                                border: selectedTheme === theme ? '2px solid #2a5298' : '2px solid transparent'
                              }}
                            />
                            <small className="text-muted d-block text-center mt-1">
                              Template {index + 1}
                            </small>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </div>
                )}

                {/* Snapshot Selection */}
                {setupMode === 'snapshot' && (
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="fw-semibold mb-0">
                        <i className="bi bi-camera"></i> Snapshot Seçimi
                      </h6>
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={() => document.getElementById('snapshotFileInput')?.click()}
                          disabled={isUploading}
                        >
                          <i className="bi bi-cloud-upload"></i> Snapshot Yükle
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={loadSnapshots}
                          disabled={isLoadingSnapshots}
                        >
                          <i className="bi bi-arrow-clockwise"></i> Yenile
                        </Button>
                      </div>
                    </div>

                    {/* File Upload Section */}
                    <div className="mb-4 p-3 border rounded bg-light">
                      <input
                        type="file"
                        id="snapshotFileInput"
                        accept=".zip"
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                      />

                      {uploadFile && (
                        <div className="mb-3">
                          <div className="d-flex align-items-center justify-content-between mb-2">
                            <div>
                              <i className="bi bi-file-earmark-zip text-primary"></i>
                              <strong className="ms-2">{uploadFile.name}</strong>
                              <small className="text-muted ms-2">
                                ({(uploadFile.size / 1024 / 1024).toFixed(2)} MB)
                              </small>
                            </div>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => {
                                setUploadFile(null);
                                setUploadProgress(0);
                                const fileInput = document.getElementById('snapshotFileInput') as HTMLInputElement;
                                if (fileInput) fileInput.value = '';
                              }}
                              disabled={isUploading}
                            >
                              <i className="bi bi-x"></i>
                            </Button>
                          </div>

                          {isUploading && (
                            <div className="mb-2">
                              <div className="progress">
                                <div
                                  className="progress-bar progress-bar-striped progress-bar-animated"
                                  role="progressbar"
                                  style={{ width: `${uploadProgress}%` }}
                                  aria-valuenow={uploadProgress}
                                  aria-valuemin={0}
                                  aria-valuemax={100}
                                >
                                  {uploadProgress}%
                                </div>
                              </div>
                            </div>
                          )}

                          <Button
                            variant="success"
                            size="sm"
                            onClick={handleUpload}
                            disabled={isUploading}
                          >
                            {isUploading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Yükleniyor...
                              </>
                            ) : (
                              <>
                                <i className="bi bi-upload"></i> Yükle
                              </>
                            )}
                          </Button>
                        </div>
                      )}

                      {!uploadFile && (
                        <div className="text-center text-muted">
                          <i className="bi bi-cloud-upload" style={{ fontSize: '2rem' }}></i>
                          <p className="mt-2 mb-0">
                            <small>
                              ZIP dosyası yüklemek için yukarıdaki "Snapshot Yükle" butonuna tıklayın<br />
                              <small className="text-muted">Maksimum dosya boyutu: 200MB</small>
                            </small>
                          </p>
                        </div>
                      )}
                    </div>

                    {isLoadingSnapshots && (
                      <div className="text-center py-3">
                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                          <span className="visually-hidden">Yükleniyor...</span>
                        </div>
                        <div className="mt-2"><small>Snapshot'lar yükleniyor...</small></div>
                      </div>
                    )}

                    {!isLoadingSnapshots && snapshots.length === 0 && (
                      <div className="text-center py-4">
                        <i className="bi bi-camera-fill text-muted" style={{ fontSize: '2rem' }}></i>
                        <p className="text-muted mt-2">
                          Henüz snapshot bulunamadı.<br />
                          <small>Önce mevcut bir demo siteden snapshot oluşturun.</small>
                        </p>
                      </div>
                    )}

                    {!isLoadingSnapshots && snapshots.length > 0 && (
                      <Row>
                        {snapshots.map(snapshot => (
                          <Col key={snapshot.name} md={4} className="mb-3">
                            <div 
                              className={`snapshot-option ${selectedSnapshot === snapshot.name ? 'selected' : ''}`}
                              onClick={() => setSelectedSnapshot(snapshot.name)}
                              style={{ cursor: 'pointer' }}
                            >
                              <div
                                className="snapshot-preview"
                                style={{
                                  backgroundColor: '#f8f9fa',
                                  border: selectedSnapshot === snapshot.name ? '2px solid #2a5298' : '2px solid transparent',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  position: 'relative'
                                }}
                              >
                                {snapshot.thumbnail ? (
                                  <img
                                    src={snapshot.thumbnail}
                                    alt={snapshot.displayName}
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'contain'
                                    }}
                                  />
                                ) : (
                                  <i className="bi bi-camera-fill" style={{ fontSize: '1.5rem', color: '#6c757d' }}></i>
                                )}
                                <div style={{ position: 'absolute', top: '8px', right: '8px', display: 'flex', gap: '5px' }}>
                                  {snapshot.isLocked && (
                                    <div
                                      className="badge bg-warning text-dark"
                                      style={{
                                        fontSize: '0.75rem',
                                        padding: '4px 8px'
                                      }}
                                      title="Bu snapshot kilitli"
                                    >
                                      <i className="bi bi-lock-fill"></i> Kilitli
                                    </div>
                                  )}
                                  <button
                                    className="btn btn-sm btn-success snapshot-download-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDownloadSnapshot(snapshot);
                                    }}
                                    style={{
                                      opacity: 0.8,
                                      transition: 'opacity 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                                    onMouseLeave={(e) => e.currentTarget.style.opacity = '0.8'}
                                    title="İndir"
                                  >
                                    <i className="bi bi-download"></i>
                                  </button>
                                  {!snapshot.isLocked && (
                                    <>
                                      <button
                                        className="btn btn-sm btn-light snapshot-edit-btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openEditModal(snapshot);
                                        }}
                                        style={{
                                          opacity: 0.8,
                                          transition: 'opacity 0.2s'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                                        onMouseLeave={(e) => e.currentTarget.style.opacity = '0.8'}
                                        title="Düzenle"
                                      >
                                        <i className="bi bi-pencil"></i>
                                      </button>
                                      <button
                                        className="btn btn-sm btn-danger snapshot-delete-btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openDeleteConfirm(snapshot);
                                        }}
                                        style={{
                                          opacity: 0.8,
                                          transition: 'opacity 0.2s'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                                        onMouseLeave={(e) => e.currentTarget.style.opacity = '0.8'}
                                        title="Sil"
                                      >
                                        <i className="bi bi-trash"></i>
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                              <div className="snapshot-name">{snapshot.displayName}</div>
                              <div className="snapshot-date text-muted">
                                {formatDate(snapshot.createdAt)}
                              </div>
                            </div>
                          </Col>
                        ))}
                      </Row>
                    )}
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>
          
          <div className="text-center">
            <Button
              type="submit"
              variant="primary"
              size="lg"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <i className="bi bi-hourglass-split"></i> İşleniyor...
                </>
              ) : (
                <>
                  <i className="bi bi-rocket"></i> Demo Site Oluştur
                </>
              )}
            </Button>
          </div>
        </Form>
        
        <div className="text-center mt-4">
          <small className="text-muted">
            <i className="bi bi-info-circle"></i> Geçerli kullanıcı bilgileri ile giriş yapın
          </small>
        </div>
      </Card.Body>
    </Card>

    {/* Edit Snapshot Modal */}
    <Modal show={showEditModal} onHide={closeEditModal} centered>
      <Modal.Header closeButton>
        <Modal.Title>Snapshot Düzenle</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Snapshot İsmi</Form.Label>
            <Form.Control
              type="text"
              value={editSnapshotName}
              onChange={(e) => setEditSnapshotName(e.target.value)}
              placeholder="Snapshot ismi"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Snapshot Görseli</Form.Label>
            <Form.Control
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              onChange={(e) => {
                const file = (e.target as HTMLInputElement).files?.[0];
                setEditSnapshotImage(file || null);
              }}
            />
            <Form.Text className="text-muted">
              PNG, JPEG veya WebP formatında görsel yükleyebilirsiniz
            </Form.Text>
          </Form.Group>

          {editingSnapshot?.thumbnail && !editSnapshotImage && (
            <div className="mb-3">
              <Form.Label>Mevcut Görsel</Form.Label>
              <div>
                <img
                  src={editingSnapshot.thumbnail}
                  alt={editingSnapshot.displayName}
                  style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain' }}
                />
              </div>
            </div>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={closeEditModal} disabled={isEditingSnapshot}>
          İptal
        </Button>
        <Button variant="primary" onClick={handleEditSnapshot} disabled={isEditingSnapshot}>
          {isEditingSnapshot ? (
            <>
              <i className="bi bi-hourglass-split"></i> Güncelleniyor...
            </>
          ) : (
            <>
              <i className="bi bi-check-circle"></i> Güncelle
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>

    {/* Delete Confirmation Modal */}
    <Modal show={showDeleteConfirm} onHide={closeDeleteConfirm} centered>
      <Modal.Header closeButton>
        <Modal.Title>Snapshot Sil</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="text-center">
          <i className="bi bi-exclamation-triangle text-warning" style={{ fontSize: '3rem' }}></i>
          <h5 className="mt-3">Silmek istediğinize emin misiniz?</h5>
          <p className="text-muted">
            <strong>{deletingSnapshot?.displayName}</strong> snapshot'ı kalıcı olarak silinecektir.
            Bu işlem geri alınamaz.
          </p>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={closeDeleteConfirm} disabled={isDeletingSnapshot}>
          İptal
        </Button>
        <Button variant="danger" onClick={handleDeleteSnapshot} disabled={isDeletingSnapshot}>
          {isDeletingSnapshot ? (
            <>
              <i className="bi bi-hourglass-split"></i> Siliniyor...
            </>
          ) : (
            <>
              <i className="bi bi-trash"></i> Evet, Sil
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  </>
  );
};

export default CreateDemo;